import { useFaultStudyCombined } from '../../hooks/useFaultAnalysis.ts'
import { NEUTRAL_BADGE_CLASS, SetupDataChip } from './indicative.tsx'

interface DutyRollupChipsProps {
  feederId: number
}

/** Anchor id on the Device Duty card wrapper in EngineeringDashboard — the
 *  rollup chips scroll here. */
export const DEVICE_DUTY_CARD_ID = 'device-duty-card'

/** Colored chip classes per duty bucket. */
const CHIP_CLASSES: Record<string, string> = {
  inadequate: 'bg-red-100 text-red-700',
  marginal: 'bg-yellow-100 text-yellow-800',
  unknown: 'bg-gray-100 text-gray-600',
}

/** D-1: the fault tab's headline severity. Replaces the removed "Nodes > 8 kA"
 *  card as the tab's interpretation surface — severity comes from device
 *  ratings vs available fault current, not a constant threshold. Reads the
 *  same combined query as the panels below (same queryKey — no extra network
 *  request) and renders nothing while loading or on error (the panels below
 *  own those states). */
export function DutyRollupChips({ feederId }: DutyRollupChipsProps) {
  const { data } = useFaultStudyCombined(feederId, r => r.duty)
  if (!data || data.total_devices === 0) return null

  // D-1 rollup vocabulary: "N inadequate / N marginal / N unknown".
  const buckets = [
    { key: 'inadequate', count: data.inadequate_count },
    { key: 'marginal', count: data.marginal_count },
    { key: 'unknown', count: data.unknown_count },
  ].filter(b => b.count > 0)

  function scrollToDuty() {
    document.getElementById(DEVICE_DUTY_CARD_ID)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-700">
          Device duty
          {data.indicative_only && <SetupDataChip />}
        </span>
        {buckets.length === 0 ? (
          <span
            className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
              data.indicative_only ? NEUTRAL_BADGE_CLASS : 'bg-green-100 text-green-700'
            }`}
          >
            All {data.total_devices} devices adequate
          </span>
        ) : (
          buckets.map(b => (
            <button
              key={b.key}
              onClick={scrollToDuty}
              title="Jump to the Device Duty card"
              className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 ${
                data.indicative_only ? NEUTRAL_BADGE_CLASS : CHIP_CLASSES[b.key]
              }`}
            >
              {b.count} {b.key}
            </button>
          ))
        )}
      </div>
    </div>
  )
}
